import toast from 'react-hot-toast';
import { useInfiniteQuery } from 'react-query';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { useWorkspaceContext } from 'hooks/common';
import {
  FilterParameters,
  generateFilterExpression,
  generateIncludeQuery,
  generatePaginationQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { fetchWrapper } from 'utils/fetchWrapper';

import { filterIncludedItem } from 'utilities/hooks/responseHandlers';
import type { Plugins, PluginsResponse } from 'types/Plugins';
import type { PluginConnections } from 'types/PluginConnections';
import type { PluginAuthentication } from 'types/PluginAuthentications';

const getPlugins = (
  workSpaceID: string | null | undefined,
  pageParam: number,
  queryString: string | null | undefined
) => {
  if (!workSpaceID) {
    return Promise.reject('workspace id is required');
  }

  const filters: FilterParameters[] = [
    {
      key: 'workspaceID',
      value: workSpaceID,
      type: 'equal',
    },
    {
      type: 'greaterThan',
      key: 'count(pluginAuthentications)',
      value: '0',
    },
  ];

  if (queryString) {
    filters.push({
      key: 'name',
      value: queryString,
      type: 'contains',
    });
  }

  const queryArray = [
    generateFilterExpression('and', filters),
    generateIncludeQuery(['pluginConnections', 'pluginAuthentications']),
    generatePaginationQuery({
      pageNumber: pageParam,
      pageSize: 10,
    }),
  ];

  const url = generateURL(SPARK_RESOURCE_TYPES.PLUGINS, queryArray);

  return fetchWrapper<PluginsResponse>({
    url,
  });
};

export const useInfinitePlugins = (query: string) => {
  const { currentWorkspaceID } = useWorkspaceContext();

  const { data: pluginResponse, ...rest } = useInfiniteQuery({
    queryKey: ['joined-plugins-connections', query],
    queryFn: ({ pageParam = 1 }) =>
      getPlugins(currentWorkspaceID, pageParam, query),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.data.length ? allPages.length + 1 : undefined;
    },
    onError: (error) => {
      if (error instanceof Error) {
        toast.error(`Failed to fetch Plugins: ${error.message}`);
      }
    },
    enabled: !!currentWorkspaceID,
  });

  const plugins = pluginResponse?.pages.reduce<
    {
      plugin: Plugins;
      pluginConnections: number;
      pluginAuthentications: PluginAuthentication[] | undefined;
    }[]
  >((acc, page) => {
    if (!page.data) {
      return acc;
    }
    const connections = filterIncludedItem<PluginConnections>(
      page.included,
      SPARK_RESOURCE_TYPES.PLUGIN_CONNECTIONS
    );

    const pluginAuthentications = filterIncludedItem<PluginAuthentication>(
      page.included,
      SPARK_RESOURCE_TYPES.PLUGIN_AUTHENTICATION
    );

    const plugin = page.data.map((plugin) => {
      const pluginID = plugin.id;
      const pluginConnections = connections?.filter((connection) => {
        const connectionAttributes = connection.attributes;
        return (
          connectionAttributes.pluginID === pluginID &&
          connectionAttributes.workspaceID === currentWorkspaceID
        );
      });
      const pluginAuthenticationFiltered = pluginAuthentications?.filter(
        (pluginAuth) => pluginAuth.attributes.pluginID === pluginID
      );
      return {
        plugin: plugin,
        pluginAuthentications: pluginAuthenticationFiltered,
        pluginConnections: pluginConnections?.length ?? 0,
      };
    });
    return [...acc, ...plugin];
  }, []);

  return {
    plugins,
    ...rest,
  };
};
